/*
 * Earth & Blossoms - agent / accessibility fixes
 * Added 8 Sep 2026. Loaded site-wide from snippets/meta-tags.liquid.
 *
 * 1. Quantity input on the product page.
 *    Dawn's quantity <label> hides its own visible text with aria-hidden
 *    and points at a visually-hidden span with aria-labelledby. Strict
 *    checkers (Shopify's agentic-browsing audit among them) do not follow
 *    that indirection and report the input as unlabelled.
 *    Fix: put the name directly on the input with aria-label.
 *
 * 2. Air Reviews rating bars.
 *    The app renders <li role="button" tabindex="0">, a role an <li> is not
 *    allowed to carry, which also breaks the list semantics of its parent
 *    <ul>. Fix: move the contents into a real <button> inside the <li> and
 *    give the <li> its listitem role back. Raise this with Air Reviews
 *    support - when they fix it upstream, delete this block.
 *
 * 3. Icon-only links whose label is hidden by CSS.
 *    The ss-header-11 section renders the search and account links as an
 *    icon plus a text span that is display:none on desktop, so at desktop
 *    widths those links have no accessible name at all (the cart link beside
 *    them does have one). That is why the desktop audit scored 3/4 on agentic
 *    browsing and 95 on accessibility while mobile scored 4/4 and 99.
 *    Fix: where an icon link has no name and its only text is unrendered,
 *    promote that text to aria-label. Proper fix is an aria-label in
 *    sections/ss-header-11.liquid, but that file is 98 KB of app-generated
 *    code - not worth rewriting wholesale for two attributes.
 *
 * Performance notes:
 *  - Timers, not requestAnimationFrame: rAF is paused in background tabs,
 *    which left the patch unapplied until the tab was focused.
 *  - Layout reads are batched before any writes and only happen for elements
 *    that contain an <svg>, so this script never thrashes layout.
 */
(function () {
  'use strict';

  var STYLE_ID = 'eb-a11y-style';
  var SHEET = [
    'li.eb-a11y-listitem{display:block}',
    'button[data-eb-a11y]{all:unset;box-sizing:border-box;display:flex;',
    'flex-direction:row;align-items:center;justify-content:space-between;',
    'width:100%;cursor:pointer}',
    'button[data-eb-a11y]:focus-visible{outline:2px solid currentColor;',
    'outline-offset:2px}'
  ].join('');

  function injectStyle() {
    if (document.getElementById(STYLE_ID)) return;
    var style = document.createElement('style');
    style.id = STYLE_ID;
    style.textContent = SHEET;
    (document.head || document.documentElement).appendChild(style);
  }

  function hasName(el) {
    return !!(
      el.getAttribute('aria-label') ||
      el.getAttribute('aria-labelledby') ||
      el.getAttribute('title')
    );
  }

  function tidy(text) {
    return (text || '').replace(/\s+/g, ' ').trim();
  }

  /* 1. Name the quantity inputs. */
  function labelQuantityInputs() {
    var inputs = document.querySelectorAll('input.quantity__input');
    for (var i = 0; i < inputs.length; i++) {
      var input = inputs[i];
      if (input.getAttribute('aria-label')) continue;

      var name = '';
      if (input.id) {
        var label = document.querySelector(
          'label[for="' + window.CSS.escape(input.id) + '"]'
        );
        if (label) {
          var ref = label.getAttribute('aria-labelledby');
          var target = ref ? document.getElementById(ref) : null;
          name = (target ? target.textContent : label.textContent) || '';
        }
      }
      input.setAttribute('aria-label', tidy(name) || 'Quantity');
    }
  }

  /* 2. Give role="button" list items a real button. */
  function fixListItemButtons() {
    var items = document.querySelectorAll('li[role="button"]');
    for (var i = 0; i < items.length; i++) {
      var li = items[i];
      try {
        if (li.querySelector('button[data-eb-a11y]')) continue;

        var button = document.createElement('button');
        button.type = 'button';
        button.setAttribute('data-eb-a11y', '');
        var label = li.getAttribute('aria-label');
        if (label) button.setAttribute('aria-label', label);

        while (li.firstChild) button.appendChild(li.firstChild);
        li.appendChild(button);

        li.classList.add('eb-a11y-listitem');
        li.removeAttribute('role');
        li.removeAttribute('tabindex');
        li.removeAttribute('aria-label');
      } catch (e) {
        /* a shim must never break the page */
      }
    }
  }

  /* 3. Name icon-only controls whose label text is not rendered. */
  function nameIconOnlyControls() {
    var candidates = document.querySelectorAll('a[href], button');
    var pending = [];

    /* Pass one: read only. innerText is layout-dependent, so every read
       happens before the first write and the browser lays out once. */
    for (var i = 0; i < candidates.length; i++) {
      var el = candidates[i];
      if (hasName(el)) continue;
      if (!el.querySelector('svg')) continue;

      var full = tidy(el.textContent);
      if (!full) continue;

      /* innerText omits text that is not rendered (display:none), so an
         empty value here means the only label is hidden from everyone. */
      if (tidy(el.innerText)) continue;

      pending.push([el, full]);
    }

    /* Pass two: write only. */
    for (var j = 0; j < pending.length; j++) {
      pending[j][0].setAttribute('aria-label', pending[j][1]);
    }
  }

  var timer = null;

  function run() {
    timer = null;
    try {
      injectStyle();
      labelQuantityInputs();
      fixListItemButtons();
      nameIconOnlyControls();
    } catch (e) {
      /* no-op */
    }
  }

  function schedule() {
    if (timer) return;
    timer = window.setTimeout(run, 50);
  }

  function observe() {
    if (!window.MutationObserver || !document.body) return;
    new MutationObserver(schedule).observe(document.body, {
      childList: true,
      subtree: true
    });
  }

  function start() {
    run();
    observe();
    /* App widgets render late; sweep a few times regardless of mutations. */
    window.setTimeout(run, 500);
    window.setTimeout(run, 1500);
    window.setTimeout(run, 3500);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }

  document.addEventListener('shopify:section:load', schedule);
  window.addEventListener('resize', schedule);
})();
